import React from 'react';
import { z } from 'zod';
import type { BlockDefinition } from '../../types';

/**
 * Kathreftis — Kathreftis marketing site v2. A faithful React rebuild of the Kathreftis
 * HTML template (jQuery → React). Each block reproduces the template's EXACT DOM
 * class names so the verbatim template CSS (served at /kathreftis/css/*, linked by
 * ZHead) styles it pixel-identically; only text/images/links are lifted into
 * editable block props (defaults = the template content). jQuery plugins (owl
 * carousel, wow.js, counters, sticky header, mobile menu, search) are
 * re-implemented as a small vanilla hydrator in apps/web (see kathreftis.ts), keyed
 * off the same class names + `data-` hooks. Asset base = /kathreftis.
 */

const A = '/kathreftis'; // asset base for bundled template images

// Rich inline text (headings carry <strong> accents in the template). Content is
// authored by admins (trusted), rendered as-is to keep the template's emphasis.
const rich = (s: string) => ({ dangerouslySetInnerHTML: { __html: s } });

// ─── Shared: stylesheet links (emitted once, inside the header) ────────────────
export const ZHead = () => (
  <>
    <link rel="stylesheet" href={`${A}/css/bootstrap.css`} />
    <link rel="stylesheet" href={`${A}/css/style.css`} />
    <link rel="stylesheet" href={`${A}/css/responsive.css`} />
    {/* Our iteration's overrides — loaded last so they win. ?v bumped to bust
        the browser cache when we change the theme. */}
    <link rel="stylesheet" href={`${A}/css/kathreftis-custom.css?v=43`} />
  </>
);

// Theme-aware logo: renders both variants; CSS shows the one for the active
// theme (dark-text on light backgrounds, white-text on dark).
function ZLogo({ light, dark, alt = 'Kathrefits AI' }: { light: string; dark: string; alt?: string }) {
  return (
    <>
      <img className="kt-logo kt-logo--light" src={light} alt={alt} />
      <img className="kt-logo kt-logo--dark" src={dark} alt={alt} />
    </>
  );
}

// Light / Dark / System switch. Wired by setupKathreftis (data-kt-theme-set).
function ThemeSwitch() {
  return (
    <div className="kt-theme" role="group" aria-label="Colour theme">
      <button type="button" className="kt-theme__btn" data-kt-theme-set="light" aria-label="Light" title="Light">
        <span className="fa fa-sun-o" aria-hidden="true">☀</span>
      </button>
      <button type="button" className="kt-theme__btn" data-kt-theme-set="dark" aria-label="Dark" title="Dark">
        <span aria-hidden="true">☾</span>
      </button>
      <button type="button" className="kt-theme__btn" data-kt-theme-set="system" aria-label="System" title="System">
        <span aria-hidden="true">◐</span>
      </button>
    </div>
  );
}

// Anti-FOUC: apply the saved/system theme before paint (no flash of light).
const THEME_BOOT = "(function(){try{var p=localStorage.getItem('kt-theme')||'system';var d=p==='dark'||(p==='system'&&window.matchMedia('(prefers-color-scheme: dark)').matches);document.documentElement.classList.toggle('kt-dark',d);}catch(e){}})();";

// ─── 1. Header ────────────────────────────────────────────────────────────────
const navChild = z.object({ label: z.string(), href: z.string().default('#') });
const navItem = z.object({
  label: z.string(),
  href: z.string().default('#'),
  children: z.array(navChild).default([]),
});
export const zHeaderSchema = z.object({
  topText: z.string().default('IT solutions that are designed to integrate multiple sectors'),
  phone: z.string().default('500.369.2580'),
  email: z.string().default('info@kathreftis.com'),
  logoUrl: z.string().default(`${A}/images/kathreftis-logo.png`),
  logoUrlDark: z.string().default(`${A}/images/kathreftis-logo.png`),
  nav: z.array(navItem).default([
    { label: 'Home', href: '/', children: [] },
    { label: 'About Us', href: '/about', children: [
      { label: 'About Us', href: '/about' },
      { label: 'Case Studies', href: '/case-studies' },
      { label: "FAQ's", href: '/faqs' },
    ] },
    { label: 'IT Solutions', href: '/services', children: [
      { label: 'All Services', href: '/services' },
      { label: 'Data Infrastructure', href: '/services' },
      { label: 'IT Cloud Integration', href: '/services' },
      { label: 'Business Security', href: '/services' },
    ] },
    { label: 'Case Studies', href: '/case-studies', children: [] },
    { label: 'News', href: '/blog', children: [] },
    { label: 'Contact', href: '/contact', children: [] },
  ]),
});
function Menu({ nav }: { nav: z.infer<typeof navItem>[] }) {
  return (
    <ul className="navigation clearfix">
      {nav.map((it, i) => (
        <li key={i} className={it.children.length ? 'dropdown' : undefined}>
          <a href={it.href}>{it.label}</a>
          {it.children.length ? (
            <ul>
              {it.children.map((c, j) => (
                <li key={j}><a href={c.href}>{c.label}</a></li>
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
function ZHeader(p: z.infer<typeof zHeaderSchema>) {
  return (
    <>
      <ZHead />
      <script dangerouslySetInnerHTML={{ __html: THEME_BOOT }} />
      <div className="preloader"><div className="icon" /></div>
      <header className="main-header header-style-one" data-kt-header>
        {/* Header Top */}
        <div className="header-top header-top-one">
          <div className="auto-container">
            <div className="inner clearfix">
              <div className="top-left clearfix"><div className="top-text">{p.topText}</div></div>
              <div className="top-right clearfix">
                <div className="info">
                  <ul className="clearfix">
                    <li className="phone"><a href={`tel:${p.phone}`}><span className="icon sl-icon-call-in" />Phone <strong>{p.phone}</strong></a></li>
                    <li className="email"><a href={`mailto:${p.email}`}><span className="icon sl-icon-envelope-open" />{p.email}</a></li>
                  </ul>
                </div>
                <div className="language">
                  <div className="lang-btn">
                    <span className="flag"><img src={`${A}/images/icons/icon-lang.png`} alt="" title="English" /></span>
                    <span className="txt">En</span>
                    <span className="arrow fa fa-angle-down" />
                  </div>
                  <div className="lang-dropdown">
                    <ul><li><a href="#">German</a></li><li><a href="#">Italian</a></li><li><a href="#">Chinese</a></li><li><a href="#">Russian</a></li></ul>
                  </div>
                </div>
                <ThemeSwitch />
              </div>
            </div>
          </div>
        </div>
        {/* Header Upper */}
        <div className="header-upper">
          <div className="auto-container">
            <div className="inner-container clearfix">
              <div className="logo-box"><div className="logo"><a href="/"><ZLogo light={p.logoUrl} dark={p.logoUrlDark} /></a></div></div>
              <div className="right-nav clearfix">
                <div className="nav-outer clearfix">
                  <div className="mobile-nav-toggler" data-kt-mobile-open><span className="icon flaticon-menu-1" /></div>
                  <nav className="main-menu navbar-expand-md navbar-light">
                    <div className="collapse navbar-collapse show clearfix" id="navbarSupportedContent">
                      <Menu nav={p.nav} />
                    </div>
                  </nav>
                </div>
                <div className="search-btn">
                  <button type="button" className="theme-btn search-toggler" data-kt-search-open><span className="fa fa-search" /></button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Sticky Header */}
        <div className="sticky-header">
          <div className="auto-container clearfix">
            <div className="logo pull-left"><a href="/"><ZLogo light={p.logoUrl} dark={p.logoUrlDark} /></a></div>
            <div className="pull-right"><nav className="main-menu clearfix"><Menu nav={p.nav} /></nav></div>
          </div>
        </div>
        {/* Mobile Menu */}
        <div className="mobile-menu">
          <div className="menu-backdrop" data-kt-mobile-close />
          <div className="close-btn" data-kt-mobile-close><span className="icon flaticon-targeting-cross" /></div>
          <nav className="menu-box">
            <div className="nav-logo"><a href="/"><ZLogo light={p.logoUrl} dark={p.logoUrlDark} /></a></div>
            <div className="menu-outer"><Menu nav={p.nav} /></div>
            <div className="social-links">
              <ul className="clearfix">
                <li><a href="#"><span className="fab fa-twitter" /></a></li>
                <li><a href="#"><span className="fab fa-facebook-square" /></a></li>
                <li><a href="#"><span className="fab fa-pinterest-p" /></a></li>
                <li><a href="#"><span className="fab fa-instagram" /></a></li>
                <li><a href="#"><span className="fab fa-youtube" /></a></li>
              </ul>
            </div>
          </nav>
        </div>
      </header>
      {/* Search Popup */}
      <div id="search-popup" className="search-popup">
        <div className="close-search theme-btn" data-kt-search-close><span className="flaticon-targeting-cross" /></div>
        <div className="popup-inner">
          <div className="overlay-layer" data-kt-search-close />
          <div className="search-form">
            <form method="post" action="#" onSubmit={(e) => e.preventDefault()}>
              <div className="form-group"><fieldset>
                <input type="search" className="form-control" name="search-input" placeholder="Search Here" required />
                <input type="submit" value="Search Now!" className="theme-btn" />
              </fieldset></div>
            </form>
            <br />
            <h3>Recent Search Keywords</h3>
            <ul className="recent-searches">
              <li><a href="#">Finance</a></li><li><a href="#">Idea</a></li><li><a href="#">Service</a></li><li><a href="#">Growth</a></li><li><a href="#">Plan</a></li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── 2. Banner (hero owl carousel) ────────────────────────────────────────────
const heroBtn = z.object({ label: z.string(), href: z.string().default('#'), style: z.enum(['one', 'two', 'video']).default('one') });
const heroSlide = z.object({
  subTitle: z.string().default('IT Solutions For Easy Integration'),
  title: z.string().default('Deliver <strong>Innovative IT Solutions</strong> For Technology Insights'),
  text: z.string().default('Building a relationship between IT companies & experts'),
  imageUrl: z.string().default(`${A}/images/ph/ph-575x650.svg`),
  alternate: z.boolean().default(false),
  buttons: z.array(heroBtn).default([
    { label: 'More Details', href: '/about', style: 'one' },
    { label: 'Watch The Demo', href: 'https://www.youtube.com/watch?v=Get7rqXYrbQ', style: 'video' },
  ]),
});
export const zBannerSchema = z.object({
  slides: z.array(heroSlide).default([
    {}, // slide 1 (defaults)
    { title: '<strong>IT Services</strong> To Develop Solutions Your Way!', imageUrl: `${A}/images/ph/ph-535x650.svg`, alternate: true, subTitle: 'IT Solutions For Easy Integration', text: 'Building a relationship between IT companies & experts', buttons: [
      { label: 'More Details', href: '/about', style: 'one' as const },
      { label: 'Our Services', href: '/services', style: 'two' as const },
    ] },
  ]),
});
function HeroButtons({ buttons }: { buttons: z.infer<typeof heroBtn>[] }) {
  return (
    <div className="links-box">
      {buttons.map((b, i) =>
        b.style === 'video' ? (
          <a key={i} href={b.href} className="theme-btn video-btn lightbox-image" data-kt-lightbox>
            <div className="btn-title"><span className="icon flaticon-play-button" /> {b.label}</div>
          </a>
        ) : (
          <a key={i} href={b.href} className={`theme-btn btn-style-${b.style === 'two' ? 'two' : 'one'}`}>
            <div className="btn-title">{b.label}</div>
          </a>
        ),
      )}
    </div>
  );
}
function ZBanner(p: z.infer<typeof zBannerSchema>) {
  return (
    <section className="banner-section banner-one">
      <div className="banner-curve" />
      <div
        className="banner-carousel theme-carousel owl-theme owl-carousel"
        data-kt-carousel
        data-options='{"loop": true, "margin": 0, "nav": true, "dots": true, "autoplay": true, "autoplayTimeout": 6000, "smartSpeed": 300, "responsive":{"0":{"items":1},"768":{"items":1},"1000":{"items":1}}}'
      >
        {p.slides.map((s, i) => (
          <div className="slide-item" key={i}>
            <div className="auto-container">
              <div className="content-box">
                <div className="round-layer" />
                <div className="content">
                  <div className={`inner${s.alternate ? ' alternate' : ''}`}>
                    <div className="sub-title">{s.subTitle}</div>
                    <h1 {...rich(s.title)} />
                    <div className="text">{s.text}</div>
                    <HeroButtons buttons={s.buttons} />
                  </div>
                </div>
                <div className="content-image"><img src={s.imageUrl} alt="" /></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── 3. About ─────────────────────────────────────────────────────────────────
export const zAboutSchema = z.object({
  upperText: z.string().default('Kathrefits - Welcome to IT Solutions'),
  heading: z.string().default('Let us be your next <strong>Preferred IT Partner</strong>'),
  body: z.string().default('Dolor sit amet, consectetur adipisicing elitm sed do eiusmod temp sed incididunt ut labore etsu dolore magna aliquatenim minim veniam quis ipsum nostrud exer citation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolorn reprehenderit voluptate velit esse.'),
  bullets: z.array(z.string()).default([
    'We are committed to providing quality IT Services',
    'Our benefits are endless for local IT Companies & Startups',
    'Really know the true needs and expectations of customers',
    'Talented & experienced management solutions for IT',
    'Processes of achieving the excellence and continue improvements',
  ]),
  images: z.array(z.string()).default([
    `${A}/images/ph/ph-230x225.svg`, `${A}/images/ph/ph-230x225.svg`,
    `${A}/images/ph/ph-230x225.svg`, `${A}/images/ph/ph-230x225.svg`,
  ]),
});
function ZAbout(p: z.infer<typeof zAboutSchema>) {
  const anim = ['fadeInLeft', 'fadeInRight', 'fadeInLeft', 'fadeInRight'];
  return (
    <>
      <section className="about-section">
        <div className="auto-container">
          <div className="row clearfix">
            <div className="text-column col-lg-6 col-md-12 col-sm-12">
              <div className="inner">
                <div className="sec-title">
                  <div className="upper-text">{p.upperText}</div>
                  <h2 {...rich(p.heading)} />
                </div>
                <div className="text-content">
                  <p>{p.body}</p>
                  <ul className="list-style-one">{p.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
                </div>
              </div>
            </div>
            <div className="image-column col-lg-6 col-md-12 col-sm-12">
              <div className="inner"><div className="images"><div className="row clearfix">
                {p.images.map((src, i) => (
                  <figure key={i} className={`image col-md-6 col-sm-6 wow ${anim[i % 4]}`} data-wow-duration="1500ms"><img src={src} alt="" /></figure>
                ))}
              </div></div></div>
            </div>
          </div>
        </div>
      </section>
      <div className="theme-separator" />
    </>
  );
}

// ─── 4. Services ──────────────────────────────────────────────────────────────
const serviceCard = z.object({
  icon: z.string(),
  title: z.string(),
  href: z.string().default('#'),
  text: z.string().default('Eiusmod tem sed incididunt labore dolore magna sed aliquatenim minim veniam quis ipsum nostrud exercitation ullamco'),
});
export const zServicesSchema = z.object({
  upperText: z.string().default('Kathrefits IT Services'),
  heading: z.string().default('<strong>Solutions And Focus Areas</strong>'),
  lowerText: z.string().default('Digital Transformation By IT Solutions'),
  services: z.array(serviceCard).default([
    { icon: `${A}/images/icons/services/1.png`, title: 'Data Infrastructure', href: '/services' },
    { icon: `${A}/images/icons/services/2.png`, title: 'IT Cloud Integration', href: '/services' },
    { icon: `${A}/images/icons/services/3.png`, title: 'IT Startup Projects', href: '/services' },
    { icon: `${A}/images/icons/services/4.png`, title: 'Product Engineering', href: '/services' },
    { icon: `${A}/images/icons/services/5.png`, title: 'Business Security', href: '/services' },
  ]),
  bottomText: z.string().default('We’re ready to discover and unlock your potential. <a href="/services"><strong>Get The Solutions</strong></a> or Call us Today! <a href="tel:258-000-3694"><strong>258-000-3694</strong></a>'),
});
function ServiceBlock({ s, delay }: { s: z.infer<typeof serviceCard>; delay: number }) {
  return (
    <div className="service-block wow fadeInUp" data-wow-delay={`${delay}ms`} data-wow-duration="1500ms">
      <div className="inner-box">
        <div className="icon-outer"><span className="icon-bg" /><div className="icon-box"><img src={s.icon} alt="" /></div></div>
        <h3><a href={s.href}>{s.title}</a></h3>
        <div className="text">{s.text}</div>
        <div className="more-link"><a href={s.href}><span className="fa fa-arrow-right" /></a></div>
      </div>
    </div>
  );
}
function ZServices(p: z.infer<typeof zServicesSchema>) {
  const s = p.services;
  return (
    <section className="services-section">
      <div className="gradient-layer" /><div className="pattern-layer" />
      <div className="auto-container">
        <div className="row clearfix">
          <div className="column col-lg-4 col-md-12 col-sm-12">
            <div className="sec-title wow fadeInUp" data-wow-duration="1500ms">
              <div className="upper-text">{p.upperText}</div>
              <h2 {...rich(p.heading)} />
              <div className="lower-text">{p.lowerText}</div>
            </div>
            {s[0] ? <ServiceBlock s={s[0]} delay={300} /> : null}
          </div>
          <div className="column col-lg-4 col-md-12 col-sm-12">
            {s[1] ? <ServiceBlock s={s[1]} delay={0} /> : null}
            {s[2] ? <ServiceBlock s={s[2]} delay={300} /> : null}
          </div>
          <div className="column col-lg-4 col-md-12 col-sm-12">
            {s[3] ? <ServiceBlock s={s[3]} delay={0} /> : null}
            {s[4] ? <ServiceBlock s={s[4]} delay={300} /> : null}
          </div>
        </div>
        <div className="bottom-text"><div className="text" {...rich(p.bottomText)} /></div>
      </div>
    </section>
  );
}

// ─── 5. Locations (map pattern) ───────────────────────────────────────────────
export const zLocationsSchema = z.object({
  upperText: z.string().default('Kathrefits IT Services'),
  heading: z.string().default('Trusted By 5M Kathrefits IT Customers Around The World'),
  lowerText: z.string().default('Sit amet consectetur adipisicing elitm sed eiusmod temp sed incididunt labore dolore magna aliquatenim veniam quis ipsum nostrud exer citation ullamco laboris.'),
  point: z.string().default('100+ Projects Done For Local IT Companies'),
});
const LOC = ['one','two','three','four','five','six','seven','eight','nine','ten','eleven','twelve'];
function ZLocations(p: z.infer<typeof zLocationsSchema>) {
  return (
    <section className="locations-section">
      <div className="auto-container">
        <div className="sec-title centered">
          <div className="upper-text">{p.upperText}</div>
          <h2>{p.heading}</h2>
          <div className="lower-text">{p.lowerText}</div>
        </div>
        <div className="map-box">
          <div className="map-image"><img src={`${A}/images/icons/map-pattern.png`} alt="" /></div>
          {LOC.map((l) => (
            <div className={`location-point loc-${l}`} key={l}><div className="inner">{p.point}</div></div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 6. Default-bg CTA ────────────────────────────────────────────────────────
export const zCtaSchema = z.object({
  heading: z.string().default('Our IT Solutions will get you on move towards your destination faster than rivals in a more reliably way!'),
  buttonLabel: z.string().default('Proven IT Solutions'),
  buttonHref: z.string().default('/about'),
  bgImageUrl: z.string().default(`${A}/images/ph/ph-1920x1280.svg`),
});
function ZCta(p: z.infer<typeof zCtaSchema>) {
  return (
    <section className="default-bg-section">
      <div className="image-layer" style={{ backgroundImage: `url(${p.bgImageUrl})` }} />
      <div className="auto-container">
        <div className="content-box">
          <h2>{p.heading}</h2>
          <div className="link-box"><a href={p.buttonHref} className="theme-btn btn-style-one"><div className="btn-title">{p.buttonLabel}</div></a></div>
        </div>
      </div>
    </section>
  );
}

// ─── 7. Fun Facts (counters) ──────────────────────────────────────────────────
const fact = z.object({ upper: z.string(), stop: z.number(), plus: z.boolean().default(false), suffix: z.string().default(''), title: z.string() });
export const zFunFactsSchema = z.object({
  facts: z.array(fact).default([
    { upper: 'System Integrations Done', stop: 1458, plus: false, suffix: '', title: 'for customers' },
    { upper: 'Powerful Team to Focus', stop: 160, plus: true, suffix: '', title: 'Expert Members' },
    { upper: 'Launched Case Studies', stop: 1080, plus: false, suffix: '', title: 'Fully Launched' },
  ]),
});
function ZFunFacts(p: z.infer<typeof zFunFactsSchema>) {
  // Size the columns so every fact fits on ONE row (bootstrap 12-col grid):
  // 3 facts → col-4, 4 → col-3, 5 → col-2 (10/12), 6 → col-2. Clamp to [2,6].
  const cols = Math.min(6, Math.max(2, Math.floor(12 / Math.max(1, p.facts.length))));
  return (
    <>
      <section className="fun-facts-section">
        <div className="auto-container">
          <div className="fact-counter"><div className="row clearfix">
            {p.facts.map((f, i) => (
              <div className={`counter-column col-lg-${cols} col-md-${cols} col-sm-6`} key={i}>
                <div className="inner wow fadeInUp" data-wow-delay={`${i * 300}ms`}>
                  <div className="upper-title">{f.upper}</div>
                  <div className="count-box"><span className="count-text" data-kt-count data-speed="1000" data-stop={f.stop}>0</span>{f.suffix}{f.plus ? '+' : ''}</div>
                  <div className="counter-title">{f.title}</div>
                </div>
              </div>
            ))}
          </div></div>
        </div>
      </section>
      <div className="theme-separator" />
    </>
  );
}

// ─── 8. Cases (owl carousel) ──────────────────────────────────────────────────
const caseCard = z.object({
  image: z.string(),
  title: z.string(),
  subText: z.string(),
  href: z.string().default('/case-studies'),
  text: z.string().default('Eiusmod sed incididunt labore magna sed aliquatenim veniam ipsum nostrud exercit.'),
  linkLabel: z.string().default('Try Now'),
});
export const zCasesSchema = z.object({
  upperText: z.string().default('Our Case Studies'),
  heading: z.string().default('<strong>IT Solutions & Projects</strong>'),
  lowerText: z.string().default('Sit amet consectetur adipisicing elitm sed eiusmod temp sed incididunt labore dolore magna aliquatenim veniam quis ipsum nostrud exer citation ullamco laboris.'),
  cases: z.array(caseCard).default([
    { image: `${A}/images/ph/ph-450x400.svg`, title: 'Workflow Management', subText: 'IT Networking' },
    { image: `${A}/images/ph/ph-450x400.svg`, title: 'Data Machine Learning', subText: 'Artificial Intelligence' },
    { image: `${A}/images/ph/ph-450x400.svg`, title: 'Workflow Management', subText: 'IT Networking' },
    { image: `${A}/images/ph/ph-450x400.svg`, title: 'Data Machine Learning', subText: 'Artificial Intelligence' },
  ]),
  bottomText: z.string().default('We’re ready to discover and unlock your potential. <strong>Call us Today!</strong> &nbsp;<a href="tel:258-000-3694"><strong>258-000-3694</strong></a>'),
});
function ZCases(p: z.infer<typeof zCasesSchema>) {
  return (
    <section className="cases-section">
      <div className="gradient-layer" />
      <div className="auto-container">
        <div className="sec-title centered">
          <div className="upper-text">{p.upperText}</div>
          <h2 {...rich(p.heading)} />
          <div className="lower-text">{p.lowerText}</div>
        </div>
        <div className="carousel-box">
          <div
            className="cases-carousel theme-carousel owl-theme owl-carousel"
            data-kt-carousel
            data-options='{"loop": true, "margin": 40, "nav": true, "dots": false, "autoplay": true, "autoplayTimeout": 7000, "smartSpeed": 700, "responsive":{"0":{"items":1},"991":{"items":1},"992":{"items":2},"1024":{"items":2}}}'
          >
            {p.cases.map((c, i) => (
              <div className="slide-item" key={i}>
                <div className="case-block"><div className="inner-box">
                  <figure className="image-box"><a href={c.href}><img src={c.image} alt="" /></a></figure>
                  <div className="content-box">
                    <div className="title-box"><h4><a href={c.href}>{c.title}</a></h4><div className="sub-text">{c.subText}</div></div>
                    <div className="text-content">
                      <div className="text">{c.text}</div>
                      <div className="link-box"><a href={c.href} target="_blank" rel="noopener noreferrer">{c.linkLabel} <span className="arrow fa fa-arrow-right" /></a></div>
                    </div>
                  </div>
                </div></div>
              </div>
            ))}
          </div>
        </div>
        <div className="bottom-text"><div className="text" {...rich(p.bottomText)} /></div>
      </div>
    </section>
  );
}

// ─── 9. Appointment (why-choose + form) ───────────────────────────────────────
const whyBlock = z.object({ icon: z.string(), title: z.string(), href: z.string().default('/services'), text: z.string().default('Sed incididunt labore dolore magna sed aliquatenim veniam quis ipsum nostrud exercitation ullamco') });
export const zAppointmentSchema = z.object({
  upperText: z.string().default('Kathrefits - Welcome to IT Solutions'),
  heading: z.string().default('Why Choose <strong>Kathrefits AI Pvt Ltd.</strong>'),
  lowerText: z.string().default('Neque porro quisquam est qui dolorem ipsum quia dolor sit amet velit sed quia non numquam eius modi tempora incidunt labore.'),
  reasons: z.array(whyBlock).default([
    { icon: `${A}/images/icons/services/6.png`, title: 'Easier To Implement', href: '/services' },
    { icon: `${A}/images/icons/services/7.png`, title: 'Increase Growth Rates', href: '/services' },
    { icon: `${A}/images/icons/services/8.png`, title: 'Branding Of The Future', href: '/services' },
  ]),
  imageUrl: z.string().default(`${A}/images/ph/ph-555x266.svg`),
  formTitle: z.string().default('Book Your Appointment'),
  formText: z.string().default('We Help Customers Digital Transformation By IT Solutions'),
  submitLabel: z.string().default('Make a Request'),
});
function ZAppointment(p: z.infer<typeof zAppointmentSchema>) {
  return (
    <section className="appointment-section">
      <div className="pattern-layer" />
      <div className="auto-container">
        <div className="row clearfix">
          <div className="text-column col-lg-6 col-md-12 col-sm-12">
            <div className="inner">
              <div className="sec-title">
                <div className="upper-text">{p.upperText}</div>
                <h2 {...rich(p.heading)} />
                <div className="lower-text">{p.lowerText}</div>
              </div>
              <div className="content">
                {p.reasons.map((r, i) => (
                  <div className="service-block-two" key={i}><div className="inner-box">
                    <div className="icon-outer"><span className="icon-bg" /><div className="icon-box"><img src={r.icon} alt="" /></div></div>
                    <h3><a href={r.href}>{r.title}</a></h3>
                    <div className="text">{r.text}</div>
                  </div></div>
                ))}
              </div>
            </div>
          </div>
          <div className="form-column col-lg-6 col-md-12 col-sm-12">
            <div className="inner wow fadeInRight" data-wow-duration="1500ms">
              <figure className="image"><img src={p.imageUrl} alt="" /></figure>
              <div className="form-box">
                <div className="title"><h3>{p.formTitle}</h3><div className="text">{p.formText}</div></div>
                <div className="default-form appointment-form">
                  {/* Posts to the shared Leads pipeline. */}
                  <form className="lf-appointment" method="post" action="/api/leads" data-lead-form noValidate>
                    <div className="row clearfix">
                      <div className="col-lg-6 col-md-6 col-sm-12 form-group"><input type="text" name="firstName" placeholder="First Name" required /></div>
                      <div className="col-lg-6 col-md-6 col-sm-12 form-group"><input type="text" name="lastName" placeholder="Last Name" required /></div>
                      <div className="col-lg-6 col-md-6 col-sm-12 form-group"><input type="email" name="email" placeholder="Email" required /></div>
                      <div className="form-group col-md-6 col-sm-12">
                        <select className="custom-select-box" name="title">
                          <option value="">Inquiry About</option>
                          <option>IT Cloud Integration</option>
                          <option>Business Security</option>
                          <option>Product Engineering</option>
                        </select>
                      </div>
                      <input type="text" name="company" defaultValue="—" hidden readOnly />
                      <input type="text" name="phone" defaultValue="—" hidden readOnly />
                      <textarea name="message" defaultValue="Appointment request submitted from the Kathreftis home page." hidden readOnly />
                      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ display: 'none' }} />
                      <div className="form-group col-md-12 col-sm-12">
                        <button type="submit" className="theme-btn btn-style-one"><span className="btn-title">{p.submitLabel}</span></button>
                      </div>
                      <div className="form-group col-md-12 col-sm-12"><p className="lf__status" data-lead-status aria-live="polite" /></div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── 10. Testimonials (owl carousel) ──────────────────────────────────────────
const testimonial = z.object({ title: z.string(), text: z.string(), name: z.string(), designation: z.string() });
export const zTestimonialsSchema = z.object({
  bgImageUrl: z.string().default(`${A}/images/ph/ph-1920x1080.svg`),
  items: z.array(testimonial).default([
    { title: 'Best Services from Kathrefits AI Pvt Ltd.!', text: 'Quis nostrud exercitation duis aute irure dolor reprehenderit voluptate. velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecas cupidatat non proident, sunt in culpa qui officia deserunt.', name: 'Saren Matthew', designation: 'CEO, IT Works' },
    { title: 'Best Services from Kathrefits AI Pvt Ltd.!', text: 'Quis nostrud exercitation duis aute irure dolor reprehenderit voluptate. velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecas cupidatat non proident, sunt in culpa qui officia deserunt.', name: 'Saren Matthew', designation: 'CEO, IT Works' },
    { title: 'Best Services from Kathrefits AI Pvt Ltd.!', text: 'Quis nostrud exercitation duis aute irure dolor reprehenderit voluptate. velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecas cupidatat non proident, sunt in culpa qui officia deserunt.', name: 'Saren Matthew', designation: 'CEO, IT Works' },
  ]),
});
function ZTestimonials(p: z.infer<typeof zTestimonialsSchema>) {
  return (
    <section className="testimonials-section">
      <div className="image-layer" style={{ backgroundImage: `url(${p.bgImageUrl})` }} />
      <div className="auto-container">
        <div className="carousel-box wow fadeInUp" data-wow-duration="1500ms">
          <div
            className="testimonial-carousel theme-carousel owl-theme owl-carousel"
            data-kt-carousel
            data-options='{"loop": true, "margin": 0, "nav": true, "dots": true, "autoplay": true, "autoplayTimeout": 5000, "smartSpeed": 500, "responsive":{"0":{"items":1},"768":{"items":1},"1024":{"items":1}}}'
          >
            {p.items.map((t, i) => (
              <div className="slide-item" key={i}>
                <div className="inner">
                  <div className="icon-box"><span className="flaticon-chat-2" /></div>
                  <div className="title">{t.title}</div>
                  <div className="text">{t.text}</div>
                  <div className="info"><div className="name">{t.name}</div><div className="designation">{t.designation}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── 11. News ─────────────────────────────────────────────────────────────────
const newsBig = z.object({ image: z.string(), category: z.string(), title: z.string(), href: z.string().default('/blog'), author: z.string().default('By Admin'), date: z.string().default('24 June 2019') });
const newsSmall = z.object({ category: z.string(), title: z.string(), href: z.string().default('/blog'), author: z.string().default('By Admin'), date: z.string().default('24 June 2019') });
export const zNewsSchema = z.object({
  upperText: z.string().default('Read Kathrefits IT News'),
  heading: z.string().default('Latest News & Articles'),
  lowerText: z.string().default('Sit amet consectetur adipisicing elitm sed eiusmod temp sed incididunt labore dolore magna aliquatenim veniam quis ipsum nostrud exer citation ullamco laboris.'),
  posts: z.array(newsBig).default([
    { image: `${A}/images/ph/ph-360x280.svg`, category: 'IT Projects', title: '10 Efficient & Measurable Benefits of Software' },
    { image: `${A}/images/ph/ph-360x280.svg`, category: 'Mobile Apps', title: 'Native or Cross-Platform: Mobile Development' },
    { image: `${A}/images/ph/ph-360x280.svg`, category: 'Development', title: 'Vital Tips For Blockchain Software Product' },
  ]),
  notes: z.array(newsSmall).default([
    { category: 'Security News', title: 'Data Security with multiple business values' },
    { category: 'Development News', title: 'Leverage the full spectrum of technology stacks' },
    { category: 'IT Sector News', title: 'Commercial apps multi-platform and multi-device strategy' },
  ]),
});
function MetaInfo({ author, date }: { author: string; date: string }) {
  return <div className="meta-info"><ul className="clearfix"><li><a href="#">{author}</a></li><li><a href="#">{date}</a></li></ul></div>;
}
function ZNews(p: z.infer<typeof zNewsSchema>) {
  return (
    <section className="news-section">
      <div className="auto-container">
        <div className="sec-title centered">
          <div className="upper-text">{p.upperText}</div>
          <h2>{p.heading}</h2>
          <div className="lower-text">{p.lowerText}</div>
        </div>
        <div className="upper-row"><div className="row clearfix">
          {p.posts.map((n, i) => (
            <div className="news-block col-lg-4 col-md-6 col-sm-12" key={i}>
              <div className="inner-box wow fadeInUp" data-wow-delay={`${i * 300}ms`} data-wow-duration="1500ms">
                <div className="image-box"><a href={n.href}><img src={n.image} alt="" /></a></div>
                <div className="lower-box">
                  <div className="category">{n.category}</div>
                  <h3><a href={n.href}>{n.title}</a></h3>
                  <MetaInfo author={n.author} date={n.date} />
                  <div className="more-link"><a href={n.href}><span className="fa fa-arrow-right" /></a></div>
                </div>
              </div>
            </div>
          ))}
        </div></div>
        <div className="lower-row"><div className="row clearfix">
          {p.notes.map((n, i) => (
            <div className="news-block-two col-lg-4 col-md-6 col-sm-12" key={i}>
              <div className="inner-box"><div className="lower-box">
                <div className="category">{n.category}</div>
                <h3><a href={n.href}>{n.title}</a></h3>
                <MetaInfo author={n.author} date={n.date} />
              </div></div>
            </div>
          ))}
        </div></div>
      </div>
    </section>
  );
}

// ─── 12. Sponsors (owl carousel) ──────────────────────────────────────────────
export const zSponsorsSchema = z.object({
  logos: z.array(z.string()).default([
    `${A}/images/ph/ph-180x150.svg`, `${A}/images/ph/ph-180x150.svg`, `${A}/images/ph/ph-180x150.svg`, `${A}/images/ph/ph-180x150.svg`, `${A}/images/ph/ph-180x150.svg`,
    `${A}/images/ph/ph-180x150.svg`, `${A}/images/ph/ph-180x150.svg`, `${A}/images/ph/ph-180x150.svg`, `${A}/images/ph/ph-180x150.svg`, `${A}/images/ph/ph-180x150.svg`,
  ]),
});
function ZSponsors(p: z.infer<typeof zSponsorsSchema>) {
  return (
    <section className="sponsors-section">
      <div className="sponsors-outer"><div className="auto-container">
        <div
          className="sponsors-carousel theme-carousel owl-theme owl-carousel"
          data-kt-carousel
          data-options='{"loop": true, "margin": 30, "nav": true, "dots": true, "autoplay": true, "autoplayTimeout": 6000, "smartSpeed": 500, "responsive":{"0":{"items":1},"600":{"items":2},"768":{"items":3},"800":{"items":3},"1024":{"items":4},"1200":{"items":5}}}'
        >
          {p.logos.map((src, i) => {
            // A logo can be an image URL/path OR a plain client NAME — render an
            // <img> for the former, a styled text pill for the latter, so the
            // "clients" carousel works either way.
            const isUrl = /^(https?:\/\/|\/)/.test(src);
            return (
              <div className="slide-item" key={i}>
                <figure className="image-box">
                  {isUrl ? <a href="#"><img src={src} alt="" /></a> : <span className="kt-client-name">{src}</span>}
                </figure>
              </div>
            );
          })}
        </div>
      </div></div>
    </section>
  );
}

// ─── 13. Footer ───────────────────────────────────────────────────────────────
const linkCol = z.object({ title: z.string(), links: z.array(z.object({ label: z.string(), href: z.string().default('#') })) });
export const zFooterSchema = z.object({
  logoUrl: z.string().default(`${A}/images/kathreftis-logo.png`),
  logoUrlDark: z.string().default(`${A}/images/kathreftis-logo.png`),
  about: z.string().default('Amet consectetur adipisicing elitm ipsum sed incididunt labore dolore magna.'),
  address: z.string().default('47 Wood Circle, Pana City FL 32401'),
  phone: z.string().default('(+1) 500.369.2580'),
  email: z.string().default('support@kathreftis.com'),
  columns: z.array(linkCol).default([
    { title: 'What We Do', links: [
      { label: 'Cloud Integration', href: '#' }, { label: 'Business Security', href: '#' }, { label: 'Startup Projects', href: '#' }, { label: 'Product Engineering', href: '#' }, { label: 'Data Infrastructure', href: '#' },
    ] },
    { title: 'About Us', links: [
      { label: 'Who We Are', href: '#' }, { label: 'Case Studies', href: '#' }, { label: 'Career Opportunities', href: '#' }, { label: 'Our Clients', href: '#' },
    ] },
    { title: 'Support', links: [
      { label: 'View Help Topics', href: '#' }, { label: 'Latest Articles', href: '#' }, { label: 'Technology Consulting', href: '#' }, { label: 'Terms Of Service', href: '#' }, { label: 'Contact Us', href: '/contact' },
    ] },
  ]),
  copyright: z.string().default('© 2020 <strong>Kathrefits AI Pvt Ltd.</strong>. All rights reserved. <a href="#">Privacy Policy</a>'),
});
function ZFooter(p: z.infer<typeof zFooterSchema>) {
  return (
    <footer className="main-footer">
      <div className="top-pattern-layer-dark" />
      <div className="widgets-section"><div className="auto-container"><div className="row clearfix">
        <div className="column col-xl-3 col-lg-12 col-md-12 col-sm-12">
          <div className="footer-widget about-widget">
            <div className="logo"><a href="#"><ZLogo light={p.logoUrl} dark={p.logoUrlDark} /></a></div>
            <div className="text">{p.about}</div>
            <div className="info"><ul>
              <li>{p.address}</li>
              <li>Call us <a href={`tel:${p.phone}`}><strong>{p.phone}</strong></a></li>
              <li><a href={`mailto:${p.email}`}>{p.email}</a></li>
            </ul></div>
          </div>
        </div>
        <div className="column col-xl-9 col-lg-12 col-md-12 col-sm-12">
          <div className="footer-widget links-widget"><div className="widget-content"><div className="row clearfix">
            <div className="col-lg-8 col-md-12 col-sm-12"><div className="row clearfix">
              {p.columns.map((col, i) => (
                <div className="column col-lg-4 col-md-4 col-sm-12" key={i}>
                  <div className="widget-title"><h4>{col.title}</h4></div>
                  <ul className="links">{col.links.map((l, j) => <li key={j}><a href={l.href}>{l.label}</a></li>)}</ul>
                </div>
              ))}
            </div></div>
            <div className="column col-lg-4 col-md-12 col-sm-12">
              <div className="widget-title empty-title"><h4>Get In Touch</h4></div>
              <div className="newsletter-form">
                <form method="post" action="#" onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group clearfix">
                    <input type="email" name="email" placeholder="Email address" required />
                    <button type="submit" className="theme-btn newsletter-btn"><span className="icon fa fa-paper-plane" /></button>
                  </div>
                </form>
              </div>
              <div className="social-links">
                <div className="title text">Get the latest news & updates</div>
                <ul className="clearfix">
                  <li><a href="#"><span className="fab fa-facebook-square" /></a></li>
                  <li><a href="#"><span className="fab fa-twitter" /></a></li>
                  <li><a href="#"><span className="fab fa-instagram" /></a></li>
                  <li><a href="#"><span className="fab fa-youtube" /></a></li>
                  <li><a href="#"><span className="fab fa-pinterest" /></a></li>
                </ul>
              </div>
            </div>
          </div></div></div>
        </div>
      </div></div></div>
      <div className="footer-bottom"><div className="auto-container"><div className="inner">
        <div className="copyright" {...rich(p.copyright)} />
      </div></div></div>
    </footer>
  );
}

// ─── Registry ─────────────────────────────────────────────────────────────────
export const kathreftisHeaderBlock: BlockDefinition = { type: 'kathreftisHeader', schema: zHeaderSchema, Renderer: ZHeader };
export const kathreftisBannerBlock: BlockDefinition = { type: 'kathreftisBanner', schema: zBannerSchema, Renderer: ZBanner };
export const kathreftisAboutBlock: BlockDefinition = { type: 'kathreftisAbout', schema: zAboutSchema, Renderer: ZAbout };
export const kathreftisServicesBlock: BlockDefinition = { type: 'kathreftisServices', schema: zServicesSchema, Renderer: ZServices };
export const kathreftisLocationsBlock: BlockDefinition = { type: 'kathreftisLocations', schema: zLocationsSchema, Renderer: ZLocations };
export const kathreftisCtaBlock: BlockDefinition = { type: 'kathreftisCta', schema: zCtaSchema, Renderer: ZCta };
export const kathreftisFunFactsBlock: BlockDefinition = { type: 'kathreftisFunFacts', schema: zFunFactsSchema, Renderer: ZFunFacts };
export const kathreftisCasesBlock: BlockDefinition = { type: 'kathreftisCases', schema: zCasesSchema, Renderer: ZCases };
export const kathreftisAppointmentBlock: BlockDefinition = { type: 'kathreftisAppointment', schema: zAppointmentSchema, Renderer: ZAppointment };
export const kathreftisTestimonialsBlock: BlockDefinition = { type: 'kathreftisTestimonials', schema: zTestimonialsSchema, Renderer: ZTestimonials };
export const kathreftisNewsBlock: BlockDefinition = { type: 'kathreftisNews', schema: zNewsSchema, Renderer: ZNews };
export const kathreftisSponsorsBlock: BlockDefinition = { type: 'kathreftisSponsors', schema: zSponsorsSchema, Renderer: ZSponsors };
export const kathreftisFooterBlock: BlockDefinition = { type: 'kathreftisFooter', schema: zFooterSchema, Renderer: ZFooter };

export const kathreftisThemeBlocks: BlockDefinition[] = [
  kathreftisHeaderBlock, kathreftisBannerBlock, kathreftisAboutBlock, kathreftisServicesBlock, kathreftisLocationsBlock, kathreftisCtaBlock,
  kathreftisFunFactsBlock, kathreftisCasesBlock, kathreftisAppointmentBlock, kathreftisTestimonialsBlock, kathreftisNewsBlock, kathreftisSponsorsBlock, kathreftisFooterBlock,
];
